import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Link2, FileSpreadsheet, BookOpen, UtensilsCrossed, CalendarCheck, Ticket } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { buildAuthHeaders } from "@/lib/http";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function Integrations() {
  const [connectedIntegrations, setConnectedIntegrations] = useState<Record<string, boolean>>({});
  const [dynamicsDialogOpen, setDynamicsDialogOpen] = useState(false);
  const [dynamicsOrgUrl, setDynamicsOrgUrl] = useState("");
  const API_URL = import.meta.env.VITE_API_URL || "https://localhost:5001/api";
  const exclusiveIntegrationIds = ["libro", "cliniko", "calendly"];

  const integrations = [
    {
      id: "google-calendar",
      name: "Google Calendar",
      icon: Calendar,
      description: "Sync appointments with your Google Calendar",
      status: "available",
      color: "text-blue-500",
    },
    {
      id: "calendly",
      name: "Calendly",
      icon: CalendarCheck,
      description: "Connect your Calendly scheduling system",
      status: "available",
      color: "text-indigo-500",
    },
    {
      id: "google-sheets",
      name: "Google Sheets",
      icon: FileSpreadsheet,
      description: "Export data to Google Sheets automatically",
      status: "available",
      color: "text-green-500",
    },
    {
      id: "cliniko",
      name: "Cliniko",
      icon: CalendarCheck,
      description: "Integrate with Cliniko appointment system",
      status: "available",
      color: "text-green-500",
    },
    {
      id: "libro",
      name: "Libro",
      icon: BookOpen,
      description: "Integrate with Libro booking system",
      status: "available",
      color: "text-purple-500",
    },
    {
      id: "dynamics",
      name: "Microsoft Dynamics",
      icon: Ticket,
      description: "Open support tickets in Dynamics 365 Customer Service",
      status: "available",
      color: "text-cyan-500",
    },
    // {
    //   id: "opentable",
    //   name: "Open Table",
    //   icon: UtensilsCrossed,
    //   description: "Connect your Open Table reservations",
    //   status: "coming-soon",
    //   color: "text-red-500",
    // },
    // {
    //   id: "nowbookit",
    //   name: "Now Book It",
    //   icon: Link2,
    //   description: "Sync with Now Book It booking platform",
    //   status: "coming-soon",
    //   color: "text-orange-500",
    // },
  ];

  useEffect(() => {
    const checkConnections = async () => {
      // Check Google Calendar
      try {
        const response = await fetch(`${API_URL}/googlecalendar/is-connected`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnectedIntegrations(prev => ({
            ...prev,
            "google-calendar": data.isConnected
          }));
        }
      } catch (error) {
        console.error("Failed to check Google Calendar connection", error);
      }
      // Check Google Sheets
      try {
        const response = await fetch(`${API_URL}/googlesheets/is-connected`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnectedIntegrations(prev => ({
            ...prev,
            "google-sheets": data.isConnected
          }));
        }
      } catch (error) {
        console.error("Failed to check Google Sheets connection", error);
      }

      // Check Calendly
      try {
        const response = await fetch(`${API_URL}/calendly/is-connected`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnectedIntegrations(prev => ({
            ...prev,
            "calendly": data.isConnected
          }));
        }
      } catch (error) {
        console.error("Failed to check Calendly connection", error);
      }

      // Check Cliniko
      try {
        const response = await fetch(`${API_URL}/cliniko/is-connected`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnectedIntegrations(prev => ({
            ...prev,
            "cliniko": data.isConnected
          }));
        }
      } catch (error) {
        console.error("Failed to check Cliniko connection", error);
      }

      // Check Libro
      try {
        const response = await fetch(`${API_URL}/libro/is-connected`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnectedIntegrations(prev => ({
            ...prev,
            "libro": data.isConnected
          }));
        }
      } catch (error) {
        console.error("Failed to check Libro connection", error);
      }

      // Check Dynamics
      try {
        const response = await fetch(`${API_URL}/dynamics/is-connected`, {
          headers: buildAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setConnectedIntegrations(prev => ({
            ...prev,
            "dynamics": data.isConnected
          }));
        }
      } catch (error) {
        console.error("Failed to check Dynamics connection", error);
      }
    };

    checkConnections();
  }, [API_URL]);

  const handleConnect = async (integrationId: string) => {
    if (integrationId === "google-calendar") {
      try {
        const response = await fetch(`${API_URL}/googlecalendar/connect`, {
          method: "GET",
          headers: buildAuthHeaders()
        });

        if (response.ok) {
          const url = await response.json();
          window.location.href = url;
        } else {
          console.error("Failed to get connect URL");
        }
      } catch (error) {
        console.error("Error connecting to Google Calendar:", error);
      }
      return;
    }

    if (integrationId === "google-sheets") {
      try {
        const response = await fetch(`${API_URL}/googlesheets/connect`, {
          method: "GET",
          headers: buildAuthHeaders()
        });

        if (response.ok) {
          const url = await response.json();
          window.location.href = url;
        } else {
          console.error("Failed to get connect URL");
        }
      } catch (error) {
        console.error("Error connecting to Google Sheets:", error);
      }
      return;
    }

    if (integrationId === "calendly") {
      try {
        const response = await fetch(`${API_URL}/calendly/connect`, {
          method: "GET",
          headers: buildAuthHeaders()
        });

        if (response.ok) {
          const url = await response.json();
          window.location.href = url;
        } else {
          console.error("Failed to get connect URL");
        }
      } catch (error) {
        console.error("Error connecting to Calendly:", error);
      }
      return;
    }

    if (integrationId === "libro") {
      try {
        const response = await fetch(`${API_URL}/libro/connect`, {
          method: "GET",
          headers: buildAuthHeaders()
        });

        if (response.ok) {
          const url = await response.json();
          window.location.href = url;
        } else {
          console.error("Failed to get Libro connect URL");
        }
      } catch (error) {
        console.error("Error connecting to Libro:", error);
      }
      return;
    }

    if (integrationId === "dynamics") {
      setDynamicsDialogOpen(true);
      return;
    }

    if (integrationId === "cliniko") {
      try {
        const response = await fetch(`${API_URL}/cliniko/connect`, {
          method: "POST",
          headers: buildAuthHeaders()
        });

        if (response.ok) {
          setConnectedIntegrations(prev => ({
            ...prev,
            cliniko: true
          }));
        } else {
          console.error("Failed to connect Cliniko");
        }
      } catch (error) {
        console.error("Error connecting to Cliniko:", error);
      }
    }
  };

  const connectDynamics = async () => {
    if (!dynamicsOrgUrl.trim()) return;
    try {
      const orgUrl = encodeURIComponent(dynamicsOrgUrl.trim().replace(/\/+$/, ""));
      const response = await fetch(`${API_URL}/dynamics/connect?orgUrl=${orgUrl}`, {
        method: "GET",
        headers: buildAuthHeaders()
      });

      if (response.ok) {
        const url = await response.json();
        window.location.href = url;
      } else {
        console.error("Failed to get Dynamics connect URL");
      }
    } catch (error) {
      console.error("Error connecting to Dynamics:", error);
    } finally {
      setDynamicsDialogOpen(false);
      setDynamicsOrgUrl("");
    }
  };

  const handleRemove = async (integrationId: string) => {
    let endpoint = "";
    if (integrationId === "google-calendar") endpoint = "googlecalendar";
    if (integrationId === "google-sheets") endpoint = "googlesheets";
    if (integrationId === "calendly") endpoint = "calendly";
    if (integrationId === "libro") endpoint = "libro";
    if (integrationId === "cliniko") endpoint = "cliniko";
    if (integrationId === "dynamics") endpoint = "dynamics";

    if (!endpoint) return;

    try {
      const response = await fetch(`${API_URL}/${endpoint}/disconnect`, {
        method: "POST",
        headers: buildAuthHeaders()
      });

      if (response.ok) {
        setConnectedIntegrations(prev => ({
          ...prev,
          [integrationId]: false
        }));
      } else {
        console.error(`Failed to disconnect ${integrationId}`);
      }
    } catch (error) {
      console.error(`Error disconnecting ${integrationId}:`, error);
    }
  };

  const exclusiveConnectedId = exclusiveIntegrationIds.find(id => connectedIntegrations[id]);

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration) => {
          const Icon = integration.icon;
          const isComingSoon = integration.status === "coming-soon";
          const isConnected = connectedIntegrations[integration.id];
          const isLockedOut = Boolean(exclusiveConnectedId && !isConnected);

          return (
            <Card
              key={integration.id}
              className={`p-6 ${isLockedOut ? "opacity-60 pointer-events-none" : "card-hover"}`}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-background border ${integration.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  {isComingSoon && (
                    <Badge variant="secondary" className="text-xs">
                      Coming Soon
                    </Badge>
                  )}
                </div>

                <h3 className="text-lg font-semibold mb-2">{integration.name}</h3>
                <p className="text-sm text-muted-foreground mb-6 flex-1">
                  {integration.description}
                </p>

                {isConnected ? (
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleRemove(integration.id)}
                    >
                      Remove
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleConnect(integration.id)}
                    >
                      Reconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant={isComingSoon ? "secondary" : "default"}
                    className="w-full"
                    disabled={isComingSoon || isLockedOut}
                    onClick={() => handleConnect(integration.id)}
                  >
                    {isComingSoon ? "Coming Soon" : `Connect ${integration.name}`}
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-6 bg-muted/50">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <Link2 className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold mb-2">Need a Custom Integration?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Contact our support team to discuss custom integrations for your specific needs.
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/support">Contact Support</Link>
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={dynamicsDialogOpen} onOpenChange={setDynamicsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Microsoft Dynamics</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Enter your Dynamics 365 organization URL to connect.
            </p>
            <Input
              placeholder="https://yourorg.crm.dynamics.com"
              value={dynamicsOrgUrl}
              onChange={(e) => setDynamicsOrgUrl(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDynamicsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={connectDynamics} disabled={!dynamicsOrgUrl.trim()}>
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
